// src/utils/passwordValidation.js - 비밀번호 검증 유틸리티

/**
 * 비밀번호 강도 검증 (Option B: 강화 보안)
 *
 * 요구사항:
 * - 최소 8자 이상
 * - 영문 대문자 1개 이상
 * - 영문 소문자 1개 이상
 * - 숫자 1개 이상
 * - 연속 숫자 4자 이상 금지 (1234, 5678)
 * - 반복 문자 4자 이상 금지 (aaaa, 1111)
 */

export const validatePassword = (password) => {
  const errors = [];
  const requirements = {
    minLength: false,
    hasUpperCase: false,
    hasLowerCase: false,
    hasNumber: false,
    noSequential: true,
    noRepeating: true
  };

  // 1. 최소 8자
  if (password.length >= 8) {
    requirements.minLength = true;
  } else {
    errors.push('최소 8자 이상 입력해주세요');
  }

  // 2. 대문자 포함
  if (/[A-Z]/.test(password)) {
    requirements.hasUpperCase = true;
  } else {
    errors.push('영문 대문자를 1개 이상 포함해야 합니다');
  }

  // 3. 소문자 포함
  if (/[a-z]/.test(password)) {
    requirements.hasLowerCase = true;
  } else {
    errors.push('영문 소문자를 1개 이상 포함해야 합니다');
  }

  // 4. 숫자 포함
  if (/[0-9]/.test(password)) {
    requirements.hasNumber = true;
  } else {
    errors.push('숫자를 1개 이상 포함해야 합니다');
  }

  // 5. 연속 숫자 4자 이상 금지 (0123, 1234, 2345, ..., 9876, 8765, ...)
  const sequentialNumbers = [
    '0123', '1234', '2345', '3456', '4567', '5678', '6789',
    '9876', '8765', '7654', '6543', '5432', '4321', '3210'
  ];

  for (const seq of sequentialNumbers) {
    if (password.includes(seq)) {
      requirements.noSequential = false;
      errors.push('연속된 숫자 4자 이상은 사용할 수 없습니다');
      break;
    }
  }

  // 6. 반복 문자 4자 이상 금지 (aaaa, 1111, AAAA)
  const repeatingPattern = /(.)\1{3,}/;
  if (repeatingPattern.test(password)) {
    requirements.noRepeating = false;
    errors.push('동일한 문자 4자 이상 반복할 수 없습니다');
  }

  // 7. 공백 금지
  if (/\s/.test(password)) {
    errors.push('공백은 사용할 수 없습니다');
  }

  const isValid = errors.length === 0;

  return {
    isValid,
    errors,
    requirements
  };
};

/**
 * 비밀번호 강도 계산
 * @returns {string} 'weak' | 'medium' | 'strong'
 */
export const getPasswordStrength = (password) => {
  if (!password) return 'weak';

  const validation = validatePassword(password);
  const { requirements } = validation;

  // 모든 요구사항 충족
  if (validation.isValid) {
    // 특수문자 포함 시 강함
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password) && password.length >= 10) {
      return 'strong';
    }
    return 'strong';
  }

  // 일부 요구사항 충족
  const fulfilledCount = Object.values(requirements).filter(Boolean).length;

  if (fulfilledCount >= 4) {
    return 'medium';
  }

  return 'weak';
};

/**
 * 비밀번호 강도 색상 반환
 */
export const getStrengthColor = (strength) => {
  switch (strength) {
    case 'strong':
      return 'text-green-600 bg-green-50 border-green-200';
    case 'medium':
      return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    case 'weak':
    default:
      return 'text-red-600 bg-red-50 border-red-200';
  }
};

/**
 * 비밀번호 강도 텍스트 반환
 */
export const getStrengthText = (strength) => {
  switch (strength) {
    case 'strong':
      return '강함 ✅';
    case 'medium':
      return '보통 ⚠️';
    case 'weak':
    default:
      return '약함 ❌';
  }
};

/**
 * 비밀번호 예시 생성
 */
export const getPasswordExamples = () => {
  return {
    good: [
      'BlueBook2024',
      'MyEnglish123',
      'Study2024abc',
      'Learn@English1'
    ],
    bad: [
      '12345678 (숫자만)',
      'password (영문만)',
      'Pass123 (8자 미만)',
      'test1234 (연속 숫자)',
      'aaaa1111 (반복 문자)'
    ]
  };
};
